import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Eye, ChevronLeft, ChevronRight, EyeOff } from "lucide-react";
import type { FlightMarkup, FlightMarkupItem } from "../types";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { markupsStatus, markupsType } from "../constants";
import { cancelFlight, publishFlight } from "../api/markups";

interface FlightTableProps {
  flights: FlightMarkupItem[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onSingleFlightSelect?: (flight: FlightMarkupItem | null) => void; // новый колбэк
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  handleToggleFlight: (status: string, id: string) => Promise<void>;
}

export function FlightTable({
  flights,
  selectedIds,
  onSelectionChange,
  page,
  pageSize,
  total,
  onPageChange,
  onSingleFlightSelect,
  handleToggleFlight,
}: FlightTableProps) {
  const totalPages = Math.ceil(total / pageSize);

  const handleToggleAll = () => {
    if (selectedIds.length === flights.length) {
      onSelectionChange([]);
      onSingleFlightSelect?.(null);
    } else {
      onSelectionChange(flights.map((f) => f.id));
      onSingleFlightSelect?.(null); // выбрано несколько — не передаём
    }
  };

  const handleToggle = (id: string) => {
    const newIds = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];

    onSelectionChange(newIds);

    // Передаём данные только если выбран ровно 1 рейс
    if (newIds.length === 1) {
      const selected = flights.find((f) => f.id === newIds[0]);
      onSingleFlightSelect?.(selected ?? null);
    } else {
      onSingleFlightSelect?.(null);
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toLocaleString("ru-RU")} ₸`;
  };

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={
                  selectedIds.length === flights.length && flights.length > 0
                }
                onCheckedChange={handleToggleAll}
              />
            </TableHead>
            <TableHead>Рейс</TableHead>
            <TableHead>Маршрут</TableHead>
            <TableHead>Авиакомпания</TableHead>
            <TableHead>Дата</TableHead>
            <TableHead>Тип</TableHead>
            <TableHead>Базовая цена</TableHead>
            <TableHead>Наценка</TableHead>
            <TableHead>Итог</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead className="w-12">Открыть</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {flights.map((flight) => (
            <TableRow key={flight.id}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.includes(flight.id)}
                  onCheckedChange={() => handleToggle(flight.id)}
                />
              </TableCell>
              <TableCell className="font-medium">{flight.flightCode}</TableCell>
              <TableCell>
                {flight.fromCity} - {flight.toCity}
              </TableCell>
              <TableCell>{flight.airline}</TableCell>
              <TableCell>{flight.departureAt}</TableCell>
              <TableCell>{markupsType[flight.markupType]}</TableCell>
              <TableCell>{formatPrice(flight.basePrice)}</TableCell>
              <TableCell>
                {flight.markup !== "" ? flight.markup : "Без наценки"}
              </TableCell>
              <TableCell>{formatPrice(flight.total)}</TableCell>
              <TableCell>
                <Badge
                  className={
                    flight.status === "active"
                      ? "bg-green-100 text-green-700 hover:bg-green-100"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-100"
                  }
                >
                  {markupsStatus[flight.status]}
                </Badge>
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => handleToggleFlight(flight.status, flight.id)}
                  variant="ghost"
                  size="icon"
                >
                  {flight.status === "active" ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex justify-end items-center gap-2 mt-4">
        <span className="text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
